import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { formatDate, formatDateTime } from "@/lib/dates";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Plus, CreditCard, DollarSign, Gift, Edit, Trash2, AlertTriangle, MoreHorizontal, Trophy } from "lucide-react";
import { ECARD_FOCUS_KEY, consumeFocusSearch } from "@/lib/adminNav";
import ECardLinkedRecordsPanel from "@/components/ECardLinkedRecordsPanel";
import CRMAccountSearchDialog, { CRMAccountLite } from "@/components/CRMAccountSearchDialog";



/** ---------- Types (match your DB) ---------- */
type Driver = {
  driver_name: string;
  is_active: boolean;
};

type ECardStatus = "Activated" | "Sold" | "Lost" | "Cancelled";
type PaymentMethod = "Cash" | "EFTPOS" | "Online";
type BonusType = "PERCENT" | "PER_BASE_AMOUNT" | "FIXED_AMOUNT";

type Promo = {
  id: string;
  promo_name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;

  min_qualifying_amount: number;
  below_min_bonus_rate: number;
  max_bonus_base: number;

  bonus_type: BonusType;
  percent_rate: number | null;
  base_amount: number | null;
  bonus_per_base: number | null;
  fixed_bonus_amount: number | null;
};


type ECard = {
  id: string;
  status: ECardStatus;

  card_number: string;
  card_last4: string;

  taxicaller_account_id: string | null;

  startup_balance: number;
  startup_deducted: boolean;

  always_100_percent: boolean;

  customer_first_name: string | null;
  customer_last_name: string | null;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  postcode: string | null;
  email: string | null;

  sold_by_driver_name: string | null;
  purchase_date: string | null;

  created_at: string;
  updated_at: string;
};

type Topup = {
  id: string;
  ecard_id: string;
  topup_date: string;

  payment_method: PaymentMethod;
  topup_source: "AdminManual" | "CustomerOnline";

  topup_amount: number;
  promo_id: string | null;

  bonus_amount: number;
  apply_amount: number;

  startup_deducted_this_topup: boolean;
  applied_by_user_id: string | null;

  notes: string | null;
  created_at: string;
};

type DrawWinner = {
  id: string;
  campaign_id: string;
  topup_id: string;
  ecard_id: string;
  winner_picked_at: string;
  campaign_name?: string;
};

type Campaign = {
  id: string;
  campaign_name: string;
};


type Tag = {
  id: string;
  name: string;
  description: string | null;
  color: string;
  applies_to_card: boolean;
  applies_to_promotion: boolean;
  is_active: boolean;
};

type CardTagLink = {
  ecard_id: string;
  tag_id: string;
};



/** ---------- Helpers ---------- */
const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(
    Number.isFinite(n) ? n : 0
  );

const isoDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function computeBonus(opts: {
  topupAmount: number;
  minQualifying: number;
  belowMinRate: number;
  maxBonusBase: number;

  bonusType: BonusType;
  percentRate?: number;
  baseAmount?: number;
  bonusPerBase?: number;
  fixedBonusAmount?: number;
}) {
  const amt = Math.max(0, opts.topupAmount);
  const baseCapped = Math.min(amt, opts.maxBonusBase);

  if (amt < opts.minQualifying) {
    const bonus = baseCapped * opts.belowMinRate;
    return Math.round(bonus * 100) / 100;
  }

  if (opts.bonusType === "PERCENT") {
    const rate = Math.max(0, opts.percentRate ?? 0);
    const bonus = baseCapped * rate;
    return Math.round(bonus * 100) / 100;
  }

  if (opts.bonusType === "FIXED_AMOUNT") {
    // Flat bonus regardless of top-up amount, as long as it meets min qualifying.
    const bonus = Math.max(0, opts.fixedBonusAmount ?? 0);
    return Math.round(bonus * 100) / 100;
  }

  const base = Math.max(0.01, opts.baseAmount ?? 25);
  const per = Math.max(0, opts.bonusPerBase ?? 0);
  const units = Math.floor(baseCapped / base);
  const bonus = units * per;
  return Math.round(bonus * 100) / 100;
}


export default function AdminECardCardsPage() {
  /** ---------- Data ---------- */
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [promos, setPromos] = useState<Promo[]>([]);
  const [cards, setCards] = useState<ECard[]>([]);
  const [allTopups, setAllTopups] = useState<Topup[]>([]);
  const [drawWinners, setDrawWinners] = useState<DrawWinner[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [cardTagLinks, setCardTagLinks] = useState<CardTagLink[]>([]);

  /** ---------- UI ---------- */
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set()); // tag ids
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [topupDisplayLimit, setTopupDisplayLimit] = useState<Record<string, number>>({});


  /** ---------- Add card ---------- */
  const [newCardNumber, setNewCardNumber] = useState("");
  const [newStartup, setNewStartup] = useState("10");

  /** ---------- Edit card modal ---------- */
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<ECard | null>(null);
  const [editStatus, setEditStatus] = useState<ECardStatus>("Activated");
  const [editDriverName, setEditDriverName] = useState<string>("none");
  const [editPurchaseDate, setEditPurchaseDate] = useState<string>("");
  const [editTCAccount, setEditTCAccount] = useState<string>("");
  const [editFirst, setEditFirst] = useState<string>("");
  const [editLast, setEditLast] = useState<string>("");
  const [editEmail, setEditEmail] = useState<string>("");
  const [editAddr1, setEditAddr1] = useState<string>("");
  const [editAddr2, setEditAddr2] = useState<string>("");
  const [editAddr3, setEditAddr3] = useState<string>("");
  const [editPostcode, setEditPostcode] = useState<string>("");
  const [editAlways100Percent, setEditAlways100Percent] = useState<boolean>(false);
  const [editTagIds, setEditTagIds] = useState<Set<string>>(new Set());

  /** ---------- CRM picker (inside Edit Card modal) ---------- */
  const [crmAccountsAll, setCrmAccountsAll] = useState<CRMAccountLite[]>([]);
  const [crmPickerOpen, setCrmPickerOpen] = useState(false);
  const [crmManualOverride, setCrmManualOverride] = useState(false);



  /** ---------- Delete confirmation ---------- */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<ECard | null>(null);

  /** ---------- Topup modal ---------- */
  const [topupModalOpen, setTopupModalOpen] = useState(false);
  const [topupCard, setTopupCard] = useState<ECard | null>(null);
  const [topupAmount, setTopupAmount] = useState<string>("25");
  const [topupDate, setTopupDate] = useState<string>(isoDate(new Date()));
  const [payMethod, setPayMethod] = useState<PaymentMethod>("Cash");
  const [promoOverride, setPromoOverride] = useState<string>("auto");
  const [topupNotes, setTopupNotes] = useState<string>("");

  /** ---------- Edit Topup modal ---------- */
  const [editTopupModalOpen, setEditTopupModalOpen] = useState(false);
  const [editingTopup, setEditingTopup] = useState<Topup | null>(null);
  const [editTopupDate, setEditTopupDate] = useState<string>("");
  const [editTopupAmount, setEditTopupAmount] = useState<string>("");
  const [editTopupPayMethod, setEditTopupPayMethod] = useState<PaymentMethod>("Cash");
  const [editTopupPromoId, setEditTopupPromoId] = useState<string>("none");
  const [editTopupBonusAmount, setEditTopupBonusAmount] = useState<string>("");
  const [editTopupApplyAmount, setEditTopupApplyAmount] = useState<string>("");
  const [editTopupNotes, setEditTopupNotes] = useState<string>("");

  /** ---------- Delete topup confirmation ---------- */
  const [deleteTopupConfirmOpen, setDeleteTopupConfirmOpen] = useState(false);
  const [topupToDelete, setTopupToDelete] = useState<Topup | null>(null);

  // Get active promos only (for the dropdown when adding new topups)
  const activePromos = useMemo(() => {
    return promos.filter((p) => p.is_active);
  }, [promos]);

  // Find promo based on topup date (auto-detect) - only from active promos
  const detectedPromo = useMemo(() => {
    if (promoOverride !== "auto") {
      if (promoOverride === "none") return null;
      return promos.find((p) => p.id === promoOverride) || null;
    }
    // Auto-detect: find active promo where topupDate falls between start and end
    return activePromos.find(
      (p) => p.start_date <= topupDate && p.end_date >= topupDate
    ) || null;
  }, [promos, activePromos, promoOverride, topupDate]);

  // Create a map of topup_id -> winner info for quick lookup
  const winnersByTopupId = useMemo(() => {
    const map: Record<string, { campaignName: string; pickedAt: string }> = {};
    for (const w of drawWinners) {
      const campaign = campaigns.find((c) => c.id === w.campaign_id);
      map[w.topup_id] = {
        campaignName: campaign?.campaign_name || "Unknown Campaign",
        pickedAt: w.winner_picked_at,
      };
    }
    return map;
  }, [drawWinners, campaigns]);

  // Lookup map: ecard_id -> Tag[]

  const tagsByCard = useMemo(() => {
    const map: Record<string, Tag[]> = {};
    const tagById: Record<string, Tag> = {};
    for (const t of tags) tagById[t.id] = t;
    for (const link of cardTagLinks) {
      const t = tagById[link.tag_id];
      if (!t) continue;
      if (!map[link.ecard_id]) map[link.ecard_id] = [];
      map[link.ecard_id].push(t);
    }
    return map;
  }, [tags, cardTagLinks]);

  // Card-applicable tags (for the filter bar + edit picker)
  const cardApplicableTags = useMemo(
    () => tags.filter((t) => t.applies_to_card && t.is_active),
    [tags]
  );

  // Sort and filter cards - sorted by last 4 digits (lowest to highest)
  const filteredCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = [...cards];

    // Filter by search
    if (q) {
      result = result.filter((c) => {
        const last4 = (c.card_last4 || "").toLowerCase();
        const num = (c.card_number || "").toLowerCase();
        const fn = (c.customer_first_name || "").toLowerCase();
        const ln = (c.customer_last_name || "").toLowerCase();
        return (
          last4.includes(q) ||
          num.includes(q) ||
          fn.includes(q) ||
          ln.includes(q) ||
          `${fn} ${ln}`.includes(q)
        );
      });
    }

    // Filter by selected tags (card must have ALL selected tags)
    if (tagFilter.size > 0) {
      result = result.filter((c) => {
        const cardTagIds = new Set((tagsByCard[c.id] || []).map((t) => t.id));
        for (const tid of tagFilter) {
          if (!cardTagIds.has(tid)) return false;
        }
        return true;
      });
    }

    // Sort by last 4 digits numerically (lowest to highest)
    result.sort((a, b) => {
      const aNum = parseInt(a.card_last4 || "0", 10);
      const bNum = parseInt(b.card_last4 || "0", 10);
      return aNum - bNum;
    });

    return result;
  }, [cards, search, tagFilter, tagsByCard]);


  // Get all topups for a specific card (sorted by date)
  const getAllCardTopups = (cardId: string) => {
    return allTopups
      .filter((t) => t.ecard_id === cardId)
      .sort((a, b) => (a.topup_date < b.topup_date ? 1 : -1));
  };

  // Get limited topups for display
  const getCardTopups = (cardId: string) => {
    const limit = topupDisplayLimit[cardId] || 10;
    return getAllCardTopups(cardId).slice(0, limit);
  };

  // Get card stats
  const getCardStats = (cardId: string) => {
    const cardTopups = getAllCardTopups(cardId);
    const totalTopups = cardTopups.length;
    const totalPaid = cardTopups.reduce((sum, t) => sum + t.topup_amount, 0);
    const totalBonus = cardTopups.reduce((sum, t) => sum + t.bonus_amount, 0);
    return { totalTopups, totalPaid, totalBonus };
  };

  // Get promo name by id
  const getPromoName = (promoId: string | null) => {
    if (!promoId) return "No promo";
    const promo = promos.find((p) => p.id === promoId);
    return promo ? promo.promo_name : "Unknown promo";
  };

  // Calculate bonus preview for modal
  const bonusPreview = useMemo(() => {
    if (!topupCard) return { bonus: 0, applyAmount: 0, startupDeduct: 0 };

    const amt = Number(topupAmount) || 0;
    const promo = detectedPromo;

    // No promotion active/selected and no per-card override -- no bonus at
    // all. The below-minimum-qualifying rate only makes sense as a promo's
    // own fallback (e.g. "under $25 during this promo still gets 50%"), not
    // as a bonus that applies with no promotion in play.
    let bonus = 0;
    if (promo || topupCard.always_100_percent) {
      const minQualifying = promo?.min_qualifying_amount ?? 25;
      const belowMinRate = promo?.below_min_bonus_rate ?? 0.5;
      const maxBonusBase = promo?.max_bonus_base ?? 150;

      let bonusType: BonusType = promo?.bonus_type ?? "PERCENT";
      let percentRate = promo?.percent_rate ?? 0;
      let baseAmount = promo?.base_amount ?? 25;
      let bonusPerBase = promo?.bonus_per_base ?? 0;
      let fixedBonusAmount = promo?.fixed_bonus_amount ?? 0;

      if (topupCard.always_100_percent) {
        bonusType = "PERCENT";
        percentRate = 1.0;
      }

      bonus = computeBonus({
        topupAmount: amt,
        minQualifying,
        belowMinRate,
        maxBonusBase,
        bonusType,
        percentRate,
        baseAmount,
        bonusPerBase,
        fixedBonusAmount,
      });
    }


    const isOnline = payMethod === "Online";
    const promisedCredit = amt + bonus;

    let startupDeduct = 0;
    if (!isOnline && !topupCard.startup_deducted && topupCard.startup_balance > 0) {
      startupDeduct = Math.min(topupCard.startup_balance, promisedCredit);
    }

    const applyAmount = isOnline ? bonus : Math.max(0, promisedCredit - startupDeduct);

    return { bonus, applyAmount, startupDeduct };
  }, [topupCard, topupAmount, detectedPromo, payMethod]);

  /** ---------- Loaders ---------- */
  async function loadAll() {
    setLoading(true);
    const [dRes, pRes, cRes, tRes, wRes, campRes, tagRes, cardTagRes] = await Promise.all([
      supabase
        .from("drivers")
        .select("driver_name, is_active")
        .order("driver_name", { ascending: true }),
      supabase
        .from("ecard_promotions")
        .select("*")
        .order("start_date", { ascending: false }),
      supabase.from("ecards").select("*").order("created_at", { ascending: false }),
      supabase.from("ecard_topups").select("*").order("topup_date", { ascending: false }),
      supabase.from("ecard_draw_winners").select("*"),
      supabase.from("ecard_draw_campaigns").select("id, campaign_name"),
      supabase.from("ecard_tags").select("*").order("name", { ascending: true }),
      supabase.from("ecard_card_tags").select("ecard_id, tag_id"),
    ]);

    if (dRes.error) toast({ title: "Drivers load failed", description: dRes.error.message });
    if (pRes.error) toast({ title: "Promos load failed", description: pRes.error.message });
    if (cRes.error) toast({ title: "Cards load failed", description: cRes.error.message });
    if (tRes.error) toast({ title: "Topups load failed", description: tRes.error.message });
    if (tagRes.error) toast({ title: "Tags load failed", description: tagRes.error.message });

    setDrivers(Array.isArray(dRes.data) ? (dRes.data as Driver[]) : []);
    setPromos(Array.isArray(pRes.data) ? (pRes.data as Promo[]) : []);
    setCards(Array.isArray(cRes.data) ? (cRes.data as ECard[]) : []);
    setAllTopups(Array.isArray(tRes.data) ? (tRes.data as Topup[]) : []);
    setDrawWinners(Array.isArray(wRes.data) ? (wRes.data as DrawWinner[]) : []);
    setCampaigns(Array.isArray(campRes.data) ? (campRes.data as Campaign[]) : []);
    setTags(Array.isArray(tagRes.data) ? (tagRes.data as Tag[]) : []);
    setCardTagLinks(Array.isArray(cardTagRes.data) ? (cardTagRes.data as CardTagLink[]) : []);
    setLoading(false);
  }


  useEffect(() => {
    void loadAll();
  }, []);

  // Cross-tab navigation: pre-fill search from sessionStorage (set by
  // TM Client Coverage when "Open in eCards" is clicked).
  useEffect(() => {
    const focus = consumeFocusSearch(ECARD_FOCUS_KEY);
    if (focus) setSearch(focus);

    const onNav = (e: Event) => {
      const detail = (e as CustomEvent<{ tab: string; search?: string }>).detail;
      if (detail?.tab === "ecard" && detail.search) {
        setSearch(detail.search);
      }
    };
    window.addEventListener("app:admin-navigate", onNav);
    return () => window.removeEventListener("app:admin-navigate", onNav);
  }, []);

  // Lazy-load CRM accounts the first time the Edit Card modal opens, so the
  // TaxiCaller Account picker can show the linked CRM account name and detect
  // when a typed value doesn't match any real CRM record.
  useEffect(() => {
    if (!editModalOpen) return;
    if (crmAccountsAll.length > 0) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("crm_accounts")
        .select(
          "id, account_id, account_name, default_cost_code, traveller_full_name, is_active"
        )
        .order("account_name", { ascending: true })
        .limit(5000);
      if (cancelled) return;
      if (error) {
        console.error("CRM accounts load failed:", error);
        return;
      }
      setCrmAccountsAll((data || []) as CRMAccountLite[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [editModalOpen, crmAccountsAll.length]);

  // Find a CRM account whose account_id matches the typed/selected
  // taxicaller_account_id. Used to label the picker and warn on mismatch.
  const editTCMatch = useMemo<CRMAccountLite | null>(() => {
    const v = editTCAccount.trim();
    if (!v) return null;
    return (
      crmAccountsAll.find((a) => (a.account_id || "").trim() === v) || null
    );
  }, [editTCAccount, crmAccountsAll]);

  // Reset the override flag whenever a different card is opened.
  useEffect(() => {
    setCrmManualOverride(false);
  }, [editingCard?.id]);




  /** ---------- Actions ---------- */
  async function addCard() {
    const cardNumber = newCardNumber.trim();
    if (!cardNumber) return toast({ title: "Card number required" });

    const startup = Number(newStartup);
    const startupBalance = Number.isFinite(startup) ? startup : 0;

    const ins = await supabase
      .from("ecards")
      .insert({
        card_number: cardNumber,
        startup_balance: startupBalance,
        startup_deducted: false,
        always_100_percent: false,
        status: "Activated",
      })
      .select("*")
      .single();

    if (ins.error) {
      toast({ title: "Add card failed", description: ins.error.message });
      return;
    }

    toast({ title: "Card added" });
    setNewCardNumber("");
    await loadAll();
  }

  function openEditModal(card: ECard) {
    setEditingCard(card);
    setEditStatus(card.status);
    setEditDriverName(card.sold_by_driver_name || "none");
    setEditPurchaseDate(card.purchase_date || isoDate(new Date()));
    setEditTCAccount(card.taxicaller_account_id || "");
    setEditFirst(card.customer_first_name || "");
    setEditLast(card.customer_last_name || "");
    setEditEmail(card.email || "");
    setEditAddr1(card.address1 || "");
    setEditAddr2(card.address2 || "");
    setEditAddr3(card.address3 || "");
    setEditPostcode(card.postcode || "");
    setEditAlways100Percent(card.always_100_percent || false);
    // Pre-populate tag selection from existing links
    const existingTagIds = new Set(
      cardTagLinks.filter((l) => l.ecard_id === card.id).map((l) => l.tag_id)
    );
    setEditTagIds(existingTagIds);
    setEditModalOpen(true);
  }



  async function saveCardDetails() {
    if (!editingCard) return;

    const upd = await supabase
      .from("ecards")
      .update({
        status: editStatus,
        sold_by_driver_name: editDriverName === "none" ? null : editDriverName === "Online" ? "Online" : editDriverName,
        purchase_date: editPurchaseDate || null,
        taxicaller_account_id: editTCAccount || null,
        customer_first_name: editFirst || null,
        customer_last_name: editLast || null,
        email: editEmail || null,
        address1: editAddr1 || null,
        address2: editAddr2 || null,
        address3: editAddr3 || null,
        postcode: editPostcode || null,
        always_100_percent: editAlways100Percent,
      })
      .eq("id", editingCard.id);

    if (upd.error) {
      toast({ title: "Save failed", description: upd.error.message });
      return;
    }

    // ---- Sync tag assignments (delta diff for ecard_card_tags) ----
    const existingForCard = new Set(
      cardTagLinks.filter((l) => l.ecard_id === editingCard.id).map((l) => l.tag_id)
    );
    const desired = editTagIds;

    const toAdd: string[] = [];
    const toRemove: string[] = [];
    for (const tid of desired) if (!existingForCard.has(tid)) toAdd.push(tid);
    for (const tid of existingForCard) if (!desired.has(tid)) toRemove.push(tid);

    if (toAdd.length > 0) {
      const ins = await supabase
        .from("ecard_card_tags")
        .insert(toAdd.map((tag_id) => ({ ecard_id: editingCard.id, tag_id })));
      if (ins.error) {
        toast({ title: "Tag assign failed", description: ins.error.message });
      }
    }
    if (toRemove.length > 0) {
      const del = await supabase
        .from("ecard_card_tags")
        .delete()
        .eq("ecard_id", editingCard.id)
        .in("tag_id", toRemove);
      if (del.error) {
        toast({ title: "Tag remove failed", description: del.error.message });
      }
    }

    toast({ title: "Card updated" });
    setEditModalOpen(false);
    setEditingCard(null);
    await loadAll();
  }



  async function markCardAsLost(card: ECard) {
    const upd = await supabase
      .from("ecards")
      .update({ status: "Lost" })
      .eq("id", card.id);

    if (upd.error) {
      toast({ title: "Update failed", description: upd.error.message });
      return;
    }

    toast({ title: "Card marked as Lost" });
    await loadAll();
  }

  function confirmDeleteCard(card: ECard) {
    setCardToDelete(card);
    setDeleteConfirmOpen(true);
  }

  async function deleteCard() {
    if (!cardToDelete) return;

    // First delete all topups for this card
    const delTopups = await supabase
      .from("ecard_topups")
      .delete()
      .eq("ecard_id", cardToDelete.id);

    if (delTopups.error) {
      toast({ title: "Failed to delete topups", description: delTopups.error.message });
      return;
    }

    // Then delete the card
    const delCard = await supabase
      .from("ecards")
      .delete()
      .eq("id", cardToDelete.id);

    if (delCard.error) {
      toast({ title: "Delete failed", description: delCard.error.message });
      return;
    }

    toast({ title: "Card deleted" });
    setDeleteConfirmOpen(false);
    setCardToDelete(null);
    setEditModalOpen(false);
    setEditingCard(null);
    await loadAll();
  }

  function openTopupModal(card: ECard) {
    setTopupCard(card);
    setTopupAmount("25");
    setTopupDate(isoDate(new Date()));
    setPayMethod("Cash");
    setPromoOverride("auto");
    setTopupNotes("");
    setTopupModalOpen(true);
  }

  async function submitTopup() {
    if (!topupCard) return toast({ title: "No card selected" });

    const amt = Number(topupAmount);
    if (!Number.isFinite(amt) || amt <= 0) return toast({ title: "Topup amount must be > 0" });

    const userRes = await supabase.auth.getUser();
    const appliedBy = userRes.data.user?.id ?? null;

    const promo = detectedPromo;

    // Same rule as the preview: no promotion and no per-card override means
    // no bonus, full stop -- the below-minimum-qualifying rate is a promo
    // fallback, not a bonus that applies with no promotion in play.
    let bonus = 0;
    if (promo || topupCard.always_100_percent) {
      const minQualifying = promo?.min_qualifying_amount ?? 25;
      const belowMinRate = promo?.below_min_bonus_rate ?? 0.5;
      const maxBonusBase = promo?.max_bonus_base ?? 150;

      let bonusType: BonusType = promo?.bonus_type ?? "PERCENT";
      let percentRate = promo?.percent_rate ?? 0;
      let baseAmount = promo?.base_amount ?? 25;
      let bonusPerBase = promo?.bonus_per_base ?? 0;
      let fixedBonusAmount = promo?.fixed_bonus_amount ?? 0;

      if (topupCard.always_100_percent) {
        bonusType = "PERCENT";
        percentRate = 1.0;
      }

      bonus = computeBonus({
        topupAmount: amt,
        minQualifying,
        belowMinRate,
        maxBonusBase,
        bonusType,
        percentRate,
        baseAmount,
        bonusPerBase,
        fixedBonusAmount,
      });
    }


    const isOnline = payMethod === "Online";
    const topupSource = isOnline ? "CustomerOnline" : "AdminManual";

    let startupDeduct = 0;
    let startupDeductedThisTopup = false;
    const promisedCredit = amt + bonus;

    if (!isOnline && !topupCard.startup_deducted && topupCard.startup_balance > 0) {
      startupDeduct = Math.min(topupCard.startup_balance, promisedCredit);
      startupDeductedThisTopup = startupDeduct > 0;
    }

    const applyAmount = isOnline ? bonus : Math.max(0, promisedCredit - startupDeduct);
    const promoToSave = topupCard.always_100_percent ? null : (promo?.id ?? null);

    // Use the selected topup_date with current time
    const topupDateTime = new Date(topupDate + "T" + new Date().toTimeString().slice(0, 8)).toISOString();

    const ins = await supabase
      .from("ecard_topups")
      .insert({
        ecard_id: topupCard.id,
        topup_date: topupDateTime,
        payment_method: payMethod,
        topup_source: topupSource,
        topup_amount: amt,
        promo_id: promoToSave,
        bonus_amount: bonus,
        apply_amount: applyAmount,
        startup_deducted_this_topup: startupDeductedThisTopup,
        applied_by_user_id: appliedBy,
        notes: topupNotes || null,
      })
      .select("*")
      .single();

    if (ins.error) {
      toast({ title: "Topup add failed", description: ins.error.message });
      return;
    }

    if (startupDeductedThisTopup) {
      await supabase
        .from("ecards")
        .update({ startup_deducted: true })
        .eq("id", topupCard.id);
    }

    toast({
      title: "Topup recorded",
      description: `Bonus ${fmtMoney(bonus)} • Apply ${fmtMoney(applyAmount)}${
        startupDeductedThisTopup ? " (startup deducted)" : ""
      }`,
    });

    setTopupModalOpen(false);
    setTopupCard(null);
    await loadAll();
  }

  // Edit topup functions
  function openEditTopupModal(topup: Topup) {
    setEditingTopup(topup);
    // Extract date from topup_date
    const dateOnly = topup.topup_date.split("T")[0];
    setEditTopupDate(dateOnly);
    setEditTopupAmount(String(topup.topup_amount));
    setEditTopupPayMethod(topup.payment_method);
    setEditTopupPromoId(topup.promo_id || "none");
    setEditTopupBonusAmount(String(topup.bonus_amount));
    setEditTopupApplyAmount(String(topup.apply_amount));
    setEditTopupNotes(topup.notes || "");
    setEditTopupModalOpen(true);
  }

  async function saveTopupDetails() {
    if (!editingTopup) return;

    const amt = Number(editTopupAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast({ title: "Topup amount must be > 0" });
      return;
    }

    const bonusAmt = Number(editTopupBonusAmount) || 0;
    const applyAmt = Number(editTopupApplyAmount) || 0;

    // Use the selected date with current time
    const topupDateTime = new Date(editTopupDate + "T" + new Date().toTimeString().slice(0, 8)).toISOString();

    const upd = await supabase
      .from("ecard_topups")
      .update({
        topup_date: topupDateTime,
        topup_amount: amt,
        payment_method: editTopupPayMethod,
        promo_id: editTopupPromoId === "none" ? null : editTopupPromoId,
        bonus_amount: bonusAmt,
        apply_amount: applyAmt,
        notes: editTopupNotes || null,
      })
      .eq("id", editingTopup.id);

    if (upd.error) {
      toast({ title: "Update failed", description: upd.error.message });
      return;
    }

    toast({ title: "Top-up updated" });
    setEditTopupModalOpen(false);
    setEditingTopup(null);
    await loadAll();
  }

  function confirmDeleteTopup(topup: Topup) {
    setTopupToDelete(topup);
    setDeleteTopupConfirmOpen(true);
  }

  async function deleteTopup() {
    if (!topupToDelete) return;

    const del = await supabase
      .from("ecard_topups")
      .delete()
      .eq("id", topupToDelete.id);

    if (del.error) {
      toast({ title: "Delete failed", description: del.error.message });
      return;
    }

    toast({ title: "Top-up deleted" });
    setDeleteTopupConfirmOpen(false);
    setTopupToDelete(null);
    setEditTopupModalOpen(false);
    setEditingTopup(null);
    await loadAll();
  }

  // Load more topups
  function loadMoreTopups(cardId: string) {
    setTopupDisplayLimit((prev) => ({
      ...prev,
      [cardId]: (prev[cardId] || 10) + 10,
    }));
  }

  const getStatusColor = (status: ECardStatus) => {
    switch (status) {
      case "Activated":
        return "bg-green-100 text-green-800 border-green-300";
      case "Sold":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "Lost":
        return "bg-red-100 text-red-800 border-red-300";
      case "Cancelled":
        return "bg-gray-100 text-gray-800 border-gray-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  /** ---------- Render ---------- */
  return (
    <div className="space-y-4 p-4">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Admin • eCard • Cards
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new card - MOVED ABOVE SEARCH */}
          <div>
            <div className="text-sm font-medium mb-2">Add New Card</div>
            <div className="flex flex-wrap gap-2 items-end">
              <Input
                className="w-64"
                value={newCardNumber}
                onChange={(e) => setNewCardNumber(e.target.value)}
                placeholder="Card number"
              />
              <Input
                className="w-32"
                value={newStartup}
                onChange={(e) => setNewStartup(e.target.value)}
                placeholder="Startup ($)"
              />
              <Button onClick={addCard}>
                <Plus className="h-4 w-4 mr-1" />
                Add Card
              </Button>
            </div>
          </div>

          {/* Search - NOW BELOW ADD CARD */}
          <div className="border-t pt-4">
            <div className="text-sm font-medium mb-1">Search (last 4, name, full card number)</div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="e.g. 4821 or Smith"
              className="max-w-md"
            />
          </div>

          {/* Tag Filter */}
          {cardApplicableTags.length > 0 && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">
                  Filter by Tags
                  {tagFilter.size > 0 && (
                    <span className="text-xs text-muted-foreground ml-2">
                      ({tagFilter.size} selected — must match ALL)
                    </span>
                  )}
                </div>
                {tagFilter.size > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setTagFilter(new Set())}
                  >
                    Clear
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {cardApplicableTags.map((t) => {
                  const active = tagFilter.has(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() =>
                        setTagFilter((prev) => {
                          const next = new Set(prev);
                          if (next.has(t.id)) next.delete(t.id);
                          else next.add(t.id);
                          return next;
                        })
                      }
                      className={`text-xs px-2 py-1 rounded-md border transition-all ${
                        active
                          ? "ring-2 ring-offset-1 ring-offset-background"
                          : "opacity-70 hover:opacity-100"
                      }`}
                      style={{
                        backgroundColor: active ? t.color : "transparent",
                        borderColor: t.color,
                        color: active ? "#fff" : t.color,
                      }}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>



      <Card>
        <CardHeader>
          <CardTitle>
            Cards List ({filteredCards.length} {filteredCards.length === 1 ? "card" : "cards"})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading cards...</div>
          ) : filteredCards.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No cards found. Add a card above or adjust your search.
            </div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto space-y-2 pr-2">
              {filteredCards.map((card) => {
                const isExpanded = expandedCardId === card.id;
                const cardTopups = getCardTopups(card.id);
                const allCardTopups = getAllCardTopups(card.id);
                const stats = getCardStats(card.id);
                const customerName = [card.customer_first_name, card.customer_last_name]
                  .filter(Boolean)
                  .join(" ") || "No customer";
                const currentLimit = topupDisplayLimit[card.id] || 10;
                const hasMoreTopups = allCardTopups.length > currentLimit;

                return (
                  <Collapsible
                    key={card.id}
                    open={isExpanded}
                    onOpenChange={() => setExpandedCardId(isExpanded ? null : card.id)}
                  >
                    <div className="border rounded-lg bg-card">
                      {/* Main Row */}
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            )}
                            
                            {/* Card Number */}
                            <div className="min-w-0">
                              <div className="font-medium">****{card.card_last4}</div>
                              <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {card.card_number}
                              </div>
                            </div>

                            {/* Customer Name */}
                            <div className="hidden sm:block min-w-0 flex-1">
                              <div className="text-sm truncate">{customerName}</div>
                              {card.email && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {card.email}
                                </div>
                              )}
                            </div>

                            {/* Card Stats */}
                            <div className="hidden lg:flex gap-3 text-xs text-muted-foreground flex-shrink-0">
                              <div className="text-center">
                                <div className="font-semibold text-foreground">{stats.totalTopups}</div>
                                <div>Top-ups</div>
                              </div>
                              <div className="text-center">
                                <div className="font-semibold text-foreground">{fmtMoney(stats.totalPaid)}</div>
                                <div>Paid</div>
                              </div>
                              <div className="text-center">
                                <div className="font-semibold text-green-600">{fmtMoney(stats.totalBonus)}</div>
                                <div>Bonus</div>
                              </div>
                            </div>

                            {/* Status */}
                            <Badge className={`${getStatusColor(card.status)} flex-shrink-0`}>
                              {card.status}
                            </Badge>
                            {/* Tag chips */}
                            {(tagsByCard[card.id] || []).length > 0 && (
                              <div className="hidden md:flex gap-1 flex-shrink-0 flex-wrap max-w-[260px]">
                                {(tagsByCard[card.id] || []).map((t) => (
                                  <span
                                    key={t.id}
                                    className="text-[10px] leading-none px-1.5 py-1 rounded-md font-medium"
                                    style={{ backgroundColor: t.color, color: "#fff" }}
                                    title={t.description || t.name}
                                  >
                                    {t.name}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Badges */}
                            <div className="hidden md:flex gap-1 flex-shrink-0">
                              {card.always_100_percent && (
                                <Badge variant="secondary" className="text-xs">100%</Badge>
                              )}
                              {card.startup_balance > 0 && !card.startup_deducted && (
                                <Badge variant="outline" className="text-xs">
                                  Startup: {fmtMoney(card.startup_balance)}
                                </Badge>
                              )}
                            </div>

                          </div>

                          {/* Top Up Button */}
                          <Button
                            size="sm"
                            className="ml-2 flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTopupModal(card);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Top Up
                          </Button>
                        </div>
                      </CollapsibleTrigger>

                      {/* Expanded Content */}
                      <CollapsibleContent>
                        <div className="border-t px-4 py-3 bg-muted/30 space-y-4">
                          {/* Card Details Summary */}
                          <div className="flex flex-wrap gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Customer:</span>{" "}
                              <span className="font-medium">{customerName}</span>
                            </div>
                            {card.email && (
                              <div>
                                <span className="text-muted-foreground">Email:</span>{" "}
                                <span>{card.email}</span>
                              </div>
                            )}
                            {card.sold_by_driver_name && (
                              <div>
                                <span className="text-muted-foreground">Sold by:</span>{" "}
                                <span>{card.sold_by_driver_name}</span>
                              </div>
                            )}
                            {card.purchase_date && (
                              <div>
                                <span className="text-muted-foreground">Purchased:</span>{" "}
                                <span>{formatDate(card.purchase_date)}</span>
                              </div>
                            )}
                            {card.taxicaller_account_id && (
                              <div>
                                <span className="text-muted-foreground">TC Account:</span>{" "}
                                <span>{card.taxicaller_account_id}</span>
                              </div>
                            )}
                          </div>

                          {/* Linked Records: CRM + TM Client # */}
                          <ECardLinkedRecordsPanel
                            ecardId={card.id}
                            taxicallerAccountId={card.taxicaller_account_id}
                            pickerInitialQuery={
                              card.customer_last_name ||
                              card.customer_first_name ||
                              card.taxicaller_account_id ||
                              ""
                            }
                            onChanged={loadAll}
                          />


                          {/* Card Stats (Mobile) */}
                          <div className="flex lg:hidden gap-4 text-sm">
                            <div className="bg-muted rounded px-3 py-2">
                              <div className="font-semibold">{stats.totalTopups}</div>
                              <div className="text-xs text-muted-foreground">Top-ups</div>
                            </div>
                            <div className="bg-muted rounded px-3 py-2">
                              <div className="font-semibold">{fmtMoney(stats.totalPaid)}</div>
                              <div className="text-xs text-muted-foreground">Total Paid</div>
                            </div>
                            <div className="bg-muted rounded px-3 py-2">
                              <div className="font-semibold text-green-600">{fmtMoney(stats.totalBonus)}</div>
                              <div className="text-xs text-muted-foreground">Total Bonus</div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditModal(card)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit Card
                            </Button>
                            {card.status !== "Lost" && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                onClick={() => markCardAsLost(card)}
                              >
                                <AlertTriangle className="h-4 w-4 mr-1" />
                                Mark as Lost
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => confirmDeleteCard(card)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete Card
                            </Button>
                          </div>

                          {/* Top-ups List */}
                          <div>
                            <div className="text-sm font-medium mb-2">
                              Top-ups ({allCardTopups.length} total)
                            </div>
                            {cardTopups.length === 0 ? (
                              <div className="text-sm text-muted-foreground py-2">
                                No top-ups recorded for this card.
                              </div>
                            ) : (
                              <>
                                <div className="border rounded-md overflow-hidden">
                                  <table className="w-full text-sm">
                                    <thead className="bg-muted">
                                      <tr>
                                        <th className="text-left p-2 font-medium">Date</th>
                                        <th className="text-right p-2 font-medium">Amount</th>
                                        <th className="text-right p-2 font-medium">Bonus</th>
                                        <th className="text-right p-2 font-medium">Applied</th>
                                        <th className="text-left p-2 font-medium">Promo</th>
                                        <th className="text-left p-2 font-medium">Method</th>
                                        <th className="text-center p-2 font-medium w-16">Edit</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {cardTopups.map((topup, idx) => {
                                        const winnerInfo = winnersByTopupId[topup.id];
                                        return (
                                          <tr
                                            key={topup.id}
                                            className={`${idx % 2 === 0 ? "bg-background" : "bg-muted/30"} ${winnerInfo ? "bg-yellow-50" : ""}`}
                                          >
                                            <td className="p-2">
                                              <div className="flex items-center gap-1">
                                                {formatDateTime(topup.topup_date)}
                                                {winnerInfo && (
                                                  <Badge className="bg-yellow-500 text-yellow-900 text-xs ml-1 flex items-center gap-1">
                                                    <Trophy className="h-3 w-3" />
                                                    Winner
                                                  </Badge>
                                                )}
                                              </div>
                                              {winnerInfo && (
                                                <div className="text-xs text-yellow-700 mt-0.5">
                                                  {winnerInfo.campaignName}
                                                </div>
                                              )}
                                            </td>
                                            <td className="p-2 text-right">{fmtMoney(topup.topup_amount)}</td>
                                            <td className="p-2 text-right text-green-600">
                                              +{fmtMoney(topup.bonus_amount)}
                                            </td>
                                            <td className="p-2 text-right font-medium">
                                              {fmtMoney(topup.apply_amount)}
                                            </td>
                                            <td className="p-2">
                                              <span className="text-xs">
                                                {getPromoName(topup.promo_id)}
                                              </span>
                                            </td>
                                            <td className="p-2">
                                              <Badge variant="outline" className="text-xs">
                                                {topup.payment_method}
                                              </Badge>
                                              {topup.startup_deducted_this_topup && (
                                                <Badge variant="secondary" className="text-xs ml-1">
                                                  Startup
                                                </Badge>
                                              )}
                                            </td>
                                            <td className="p-2 text-center">
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0"
                                                onClick={() => openEditTopupModal(topup)}
                                              >
                                                <Edit className="h-3 w-3" />
                                              </Button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>

                                </div>
                                {hasMoreTopups && (
                                  <div className="mt-2 text-center">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => loadMoreTopups(card.id)}
                                    >
                                      <MoreHorizontal className="h-4 w-4 mr-1" />
                                      Load More ({allCardTopups.length - currentLimit} remaining)
                                    </Button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Card Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Card Details</DialogTitle>
          </DialogHeader>
          {editingCard && (
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge variant="outline">****{editingCard.card_last4}</Badge>
                {editingCard.always_100_percent && <Badge>Always 100%</Badge>}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <Select value={editStatus} onValueChange={(v) => setEditStatus(v as ECardStatus)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Activated">Activated</SelectItem>
                      <SelectItem value="Sold">Sold</SelectItem>
                      <SelectItem value="Lost">Lost</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-2">
                  <label className="text-sm font-medium">CRM Account (TaxiCaller link)</label>
                  <div className="flex gap-2">
                    <div className="flex-1 min-w-0 border rounded-md px-2 py-1.5 bg-background flex items-center gap-2">
                      {editTCAccount ? (
                        editTCMatch ? (
                          <>
                            <Badge className="bg-green-700 text-white text-[10px]">Linked</Badge>
                            <span className="font-mono text-xs">{editTCMatch.account_id}</span>
                            <span className="text-xs truncate">· {editTCMatch.account_name}</span>
                          </>
                        ) : (
                          <>
                            <Badge className="bg-red-600 text-white text-[10px]">No CRM match</Badge>
                            <span className="font-mono text-xs">{editTCAccount}</span>
                          </>
                        )
                      ) : (
                        <span className="text-xs text-muted-foreground italic">No CRM account linked</span>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setCrmPickerOpen(true)}
                    >
                      {editTCAccount ? "Change" : "Pick CRM"}
                    </Button>
                    {editTCAccount && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-red-600"
                        onClick={() => {
                          setEditTCAccount("");
                          setCrmManualOverride(false);
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  {editTCAccount && !editTCMatch && (
                    <div className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      No matching CRM account for ID "{editTCAccount}".{" "}
                      {!crmManualOverride && (
                        <button
                          type="button"
                          className="underline"
                          onClick={() => setCrmManualOverride(true)}
                        >
                          Keep as manual override
                        </button>
                      )}
                      {crmManualOverride && (
                        <span className="italic">Manual override active.</span>
                      )}
                    </div>
                  )}
                  {!editTCAccount && (
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      Use the CRM picker to attach a real customer account.
                    </div>
                  )}
                </div>


                <div>
                  <label className="text-sm font-medium">Sold by Driver</label>
                  <Select value={editDriverName} onValueChange={setEditDriverName}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      <SelectItem value="Online">Online</SelectItem>
                      {drivers
                        .filter((d) => d.is_active)
                        .map((d) => (
                          <SelectItem key={d.driver_name} value={d.driver_name}>
                            {d.driver_name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium">Purchase Date</label>
                  <Input
                    type="date"
                    value={editPurchaseDate}
                    onChange={(e) => setEditPurchaseDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">First Name</label>
                  <Input
                    value={editFirst}
                    onChange={(e) => setEditFirst(e.target.value)}
                    placeholder="First name"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Last Name</label>
                  <Input
                    value={editLast}
                    onChange={(e) => setEditLast(e.target.value)}
                    placeholder="Last name"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    placeholder="Email"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="text-sm font-medium">Address Line 1</label>
                  <Input
                    value={editAddr1}
                    onChange={(e) => setEditAddr1(e.target.value)}
                    placeholder="Address line 1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Address Line 2</label>
                  <Input
                    value={editAddr2}
                    onChange={(e) => setEditAddr2(e.target.value)}
                    placeholder="Address line 2"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Address Line 3</label>
                  <Input
                    value={editAddr3}
                    onChange={(e) => setEditAddr3(e.target.value)}
                    placeholder="Address line 3"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Postcode</label>
                  <Input
                    value={editPostcode}
                    onChange={(e) => setEditPostcode(e.target.value)}
                    placeholder="Postcode"
                  />
                </div>
              </div>

              {/* Tag Picker */}
              {cardApplicableTags.length > 0 && (
                <div className="border-t pt-3">
                  <label className="text-sm font-medium block mb-2">Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {cardApplicableTags.map((t) => {
                      const active = editTagIds.has(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() =>
                            setEditTagIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(t.id)) next.delete(t.id);
                              else next.add(t.id);
                              return next;
                            })
                          }
                          className={`text-xs px-2 py-1 rounded-md border transition-all ${
                            active ? "ring-2 ring-offset-1 ring-offset-background" : "opacity-70 hover:opacity-100"
                          }`}
                          style={{
                            backgroundColor: active ? t.color : "transparent",
                            borderColor: t.color,
                            color: active ? "#fff" : t.color,
                          }}
                          title={t.description || t.name}
                        >
                          {t.name}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Click tags to assign or remove. Manage tags in the eCard Tags tab.
                  </div>
                </div>
              )}

              {/* Always 100% Checkbox */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="always100percent"
                  checked={editAlways100Percent}
                  onChange={(e) => setEditAlways100Percent(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="always100percent" className="text-sm font-medium cursor-pointer">
                  Always 100% Bonus
                </label>
                <span className="text-xs text-muted-foreground">
                  (Customer always receives 100% bonus regardless of promotion)
                </span>
              </div>

              {/* Delete Button in Edit Modal */}
              <div className="border-t pt-4">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => confirmDeleteCard(editingCard)}
                  className="w-full"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete This Card
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveCardDetails}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Card Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Card?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete card ****{cardToDelete?.card_last4}? 
              This will also delete all top-up history for this card. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteCard}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Top Up Modal */}
      <Dialog open={topupModalOpen} onOpenChange={setTopupModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Apply Top Up
            </DialogTitle>
          </DialogHeader>
          {topupCard && (
            <div className="space-y-4">
              {/* Card Info */}
              <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">****{topupCard.card_last4}</span>
                  <Badge className={getStatusColor(topupCard.status)}>{topupCard.status}</Badge>
                </div>
                <div className="text-sm text-muted-foreground">
                  {[topupCard.customer_first_name, topupCard.customer_last_name]
                    .filter(Boolean)
                    .join(" ") || "No customer name"}
                </div>
                {topupCard.always_100_percent && (
                  <Badge variant="secondary" className="text-xs">Always 100% Bonus</Badge>
                )}
                {topupCard.startup_balance > 0 && !topupCard.startup_deducted && (
                  <Badge variant="outline" className="text-xs">
                    Startup Balance: {fmtMoney(topupCard.startup_balance)}
                  </Badge>
                )}
              </div>

              {/* Top Up Form */}
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Top Up Date</label>
                  <Input
                    type="date"
                    value={topupDate}
                    onChange={(e) => setTopupDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Top Up Amount</label>
                  <Input
                    type="number"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    placeholder="25"
                    min="0"
                    step="5"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Payment Method</label>
                  <Select value={payMethod} onValueChange={(v) => setPayMethod(v as PaymentMethod)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="EFTPOS">EFTPOS</SelectItem>
                      <SelectItem value="Online">Online</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium">Promotion</label>
                  <Select value={promoOverride} onValueChange={setPromoOverride}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-detect from date</SelectItem>
                      <SelectItem value="none">No promotion</SelectItem>
                      {activePromos.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.promo_name} ({p.start_date} → {p.end_date})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {promoOverride === "auto" && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {detectedPromo 
                        ? `Detected: ${detectedPromo.promo_name}` 
                        : "No active promo for selected date"}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium">Notes (optional)</label>
                  <Input
                    value={topupNotes}
                    onChange={(e) => setTopupNotes(e.target.value)}
                    placeholder="Any notes..."
                  />
                </div>
              </div>

              {/* Calculation Preview */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 text-green-800 font-medium">
                  <Gift className="h-4 w-4" />
                  Calculation Preview
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground">Top Up Amount:</div>
                  <div className="text-right font-medium">{fmtMoney(Number(topupAmount) || 0)}</div>

                  <div className="text-muted-foreground">Bonus:</div>
                  <div className="text-right font-medium text-green-600">
                    +{fmtMoney(bonusPreview.bonus)}
                  </div>

                  {bonusPreview.startupDeduct > 0 && (
                    <>
                      <div className="text-muted-foreground">Startup Deduction:</div>
                      <div className="text-right font-medium text-orange-600">
                        -{fmtMoney(bonusPreview.startupDeduct)}
                      </div>
                    </>
                  )}

                  <div className="text-muted-foreground border-t pt-1">Amount to Apply:</div>
                  <div className="text-right font-bold text-lg border-t pt-1">
                    {fmtMoney(bonusPreview.applyAmount)}
                  </div>
                </div>

                {detectedPromo && !topupCard.always_100_percent && (
                  <div className="text-xs text-muted-foreground mt-2">
                    Using promo: {detectedPromo.promo_name}
                  </div>
                )}
                {topupCard.always_100_percent && (
                  <div className="text-xs text-green-700 mt-2">
                    Card has 100% bonus override active
                  </div>
                )}
                {payMethod === "Online" && (
                  <div className="text-xs text-blue-600 mt-2">
                    Online payment: Only bonus amount will be applied to card
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopupModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitTopup}>
              <Plus className="h-4 w-4 mr-1" />
              Apply Top Up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Top Up Modal */}
      <Dialog open={editTopupModalOpen} onOpenChange={setEditTopupModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Top Up
            </DialogTitle>
          </DialogHeader>
          {editingTopup && (
            <div className="space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Top Up Date</label>
                  <Input
                    type="date"
                    value={editTopupDate}
                    onChange={(e) => setEditTopupDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Top Up Amount</label>
                  <Input
                    type="number"
                    value={editTopupAmount}
                    onChange={(e) => setEditTopupAmount(e.target.value)}
                    placeholder="25"
                    min="0"
                    step="5"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Payment Method</label>
                  <Select value={editTopupPayMethod} onValueChange={(v) => setEditTopupPayMethod(v as PaymentMethod)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="EFTPOS">EFTPOS</SelectItem>
                      <SelectItem value="Online">Online</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium">Promotion</label>
                  <Select value={editTopupPromoId} onValueChange={setEditTopupPromoId}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No promotion</SelectItem>
                      {promos.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.promo_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Bonus Amount</label>
                    <Input
                      type="number"
                      value={editTopupBonusAmount}
                      onChange={(e) => setEditTopupBonusAmount(e.target.value)}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Apply Amount</label>
                    <Input
                      type="number"
                      value={editTopupApplyAmount}
                      onChange={(e) => setEditTopupApplyAmount(e.target.value)}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Notes (optional)</label>
                  <Input
                    value={editTopupNotes}
                    onChange={(e) => setEditTopupNotes(e.target.value)}
                    placeholder="Any notes..."
                  />
                </div>
              </div>

              {/* Delete Button */}
              <div className="border-t pt-4">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => confirmDeleteTopup(editingTopup)}
                  className="w-full"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete This Top Up
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTopupModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveTopupDetails}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Top Up Confirmation Dialog */}
      <AlertDialog open={deleteTopupConfirmOpen} onOpenChange={setDeleteTopupConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Top Up?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this top-up of {fmtMoney(topupToDelete?.topup_amount || 0)}? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteTopup}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CRM Account picker for the Edit Card modal */}
      <CRMAccountSearchDialog
        open={crmPickerOpen}
        onOpenChange={setCrmPickerOpen}
        initialQuery={
          editLast || editFirst || editTCAccount || ""
        }
        onPick={(a) => {
          setEditTCAccount(a.account_id);
          setCrmManualOverride(false);
          toast({
            title: "CRM account selected",
            description: `${a.account_id} · ${a.account_name}`,
          });
        }}
      />
    </div>
  );
}
